<template>
    <page-layout>
        <g-loader v-if="isLoading" type="fixed" />
        <template v-else>
            <template slot="header">
                <page-title title="Complete Task" @on-close="onPageClose" />
            </template>
            <template slot="default">
                <g-button icon="angle-left" class="margin-bottom-large" theme="live" size="small" @click="onBack">
                    Back
                </g-button>

                <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />

                <g-typography class="margin-bottom-small" tag="h2" font-weight="semibold" :value="task.name" />
                <div v-if="task && task.description" class="margin-bottom-large">
                    <div v-html="$sanitize(task.description)"></div>
                </div>

                <div v-if="uploadType === 'file'" class="border-bottom-grey">
                    <div class="margin-bottom-normal">
                        <div class="margin-bottom-small">
                            <g-typography tag="h3" font-weight="semibold"> Deliver your content below</g-typography>
                        </div>
                    </div>
                    <g-upload
                        :action="deliverContentUrl"
                        :headers="uploadHeaders"
                        :multiple="true"
                        :show-upload-list="{ showRemoveIcon: false }"
                        @change="handleFileUpload"
                    >
                        <g-button shape="round" theme="live" style="height: 40px" class="margin-bottom-small">
                            <g-icon name="upload" />
                            Upload Content
                        </g-button>
                    </g-upload>
                    <p class="step-card-uploads-description">
                        Files Accepted: .mov, .mp4, .png, .jpg, .gif, .doc, .docx, .pdf
                    </p>
                </div>
                <div v-if="uploadType === 'text'" class="border-bottom-grey">
                    <div class="margin-bottom-normal">
                        <div class="margin-bottom-small">
                            <g-typography tag="h3" font-weight="semibold"> Deliver your content below</g-typography>
                        </div>
                    </div>
                    <g-text-area v-model="feedback" class="content-text-area" />
                    <g-button
                        shape="round"
                        theme="live"
                        style="height: 40px"
                        class="margin-bottom-normal"
                        :disabled="!feedback || isDelivering"
                        :loading="isDelivering"
                        @click="handleFeedbackUpload"
                    >
                        Submit
                    </g-button>
                </div>
                <div v-if="uploadType === 'url'" class="border-bottom-grey">
                    <div class="margin-bottom-normal">
                        <div class="margin-bottom-small">
                            <g-typography tag="h3" font-weight="semibold"> Deliver your content below</g-typography>
                        </div>
                    </div>
                    <input
                        v-model="urlText"
                        class="margin-bottom-normal input-class"
                        placeholder="Paste your content link here"
                    />
                    <g-button
                        shape="round"
                        theme="live"
                        style="height: 40px"
                        class="margin-bottom-normal"
                        :disabled="!urlText || isDelivering"
                        :loading="isDelivering"
                        @click="handleUrlUpload"
                    >
                        Submit
                    </g-button>
                </div>
            </template>
            <template>
                <div v-if="uploadType === 'file'" class="margin-bottom-normal">
                    <div v-if="!deliverable" class="margin-bottom-small null-state-container">
                        <g-icon name="upload" />
                        <g-typography tag="h3" font-weight="semibold"> Nothing has been delivered yet! </g-typography>
                    </div>
                    <div v-for="contents in content" :key="contents.content.url" class="uploads-container">
                        <div v-if="contents.content">
                            <div v-for="fileContents in contents.content" :key="fileContents.content.id">
                                <div
                                    v-if="
                                        fileContents.content.type === 'image' ||
                                        fileContents.content.type === 'video' ||
                                        fileContents.content.type === 'file'
                                    "
                                >
                                    <h3>
                                        Delivered: {{ moment(fileContents.content.created_at).format('MMM D, YYYY') }}
                                    </h3>
                                    <a :href="fileContents.content.url" class="uploads-container-description-url">
                                        {{ fileContents.content.url }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else-if="uploadType === 'text' && hasContent" class="margin-bottom-normal">
                    <div v-if="!deliverable" class="margin-bottom-small null-state-container">
                        <g-icon name="upload" />
                        <g-typography tag="h3" font-weight="semibold"> Nothing has been delivered yet! </g-typography>
                    </div>
                    <div v-for="contents in content" :key="contents.content.caption" class="uploads-container">
                        <div v-for="captionContents in contents.content" :key="captionContents.content.type">
                            <div v-if="captionContents.content.type === 'text'">
                                <h3>
                                    Delivered: {{ moment(captionContents.content.created_at).format('MMM D, YYYY') }}
                                </h3>
                                <p
                                    class="uploads-container-description"
                                    v-html="$sanitize(formatCaption(captionContents.content.caption))"
                                ></p>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else-if="uploadType === 'url' && hasContent" class="margin-bottom-normal">
                    <div v-if="!deliverable" class="margin-bottom-small null-state-container">
                        <g-icon name="upload" />
                        <g-typography tag="h3" font-weight="semibold"> Nothing has been delivered yet! </g-typography>
                    </div>
                    <div v-for="contents in content" :key="contents.created_at" class="uploads-container">
                        <div v-for="urlcontents in contents.content" :key="urlcontents.content.id">
                            <div v-if="urlcontents.content.type === 'url'">
                                <h3>Delivered: {{ moment(urlcontents.content.created_at).format('MMM D, YYYY') }}</h3>
                                <a
                                    class="uploads-container-description-url"
                                    :href="urlcontents.content.url"
                                    target="_blank"
                                    >{{ urlcontents.content.url }}</a
                                >
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </template>
    </page-layout>
</template>

<script>
    import { computed, ref } from '@vue/composition-api';
    import { get, filter } from 'lodash';
    import UploadMixin from '../../mixins/ActivationUploadsMixin';
    import PageLayout from '../../../../layouts/PageLayout.vue';
    import PageTitle from '../../../../library/PageTitle.vue';
    import ActivationBriefNetworkConnectionMixin from '../../../../providers/mixins/ActivationBriefNetworkConnectionMixin';
    import ErrorMessageCard from '../../../proposals/partials/ErrorMessageCard.vue';
    import moment from 'moment-timezone';
    import { useFeatureFlagsStore } from '../../../../../../stores';
    import useActivationBriefs from '../../../../../../src/composables/use-activation-briefs';
    import useRouter from '../../../../../../src/composables/use-router';
    import useRoute from '../../../../../../src/composables/use-route';

    export default {
        name: 'ActivationCustomTaskDeliveryView',
        components: {
            PageTitle,
            PageLayout,
            ErrorMessageCard
        },
        mixins: [ActivationBriefNetworkConnectionMixin, UploadMixin],
        props: {
            brief: {
                type: Object
            },
            contact: {
                type: Object
            },
            deliverable: {
                type: Object
            },
            task: {
                type: Object
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String,
                required: false
            }
        },
        setup(props, { emit, root }) {
            const ACTIVATION_TASKS = 'activation-tasks';
            const feedback = ref('');
            const urlText = ref('');
            const isDelivering = ref(false);
            const uploadType = computed(() => get(props, 'task.type_delivery_methods[0]'));
            const taskId = computed(() => get(props, 'task.id'));
            const deliverContentUrl = computed(() => `/activations/upload/${taskId.value}/content`);
            const deliverFeedbackUrl = computed(() => `/activations/upload/${taskId.value}/feedback`);
            const deliverUrlText = computed(() => `/activations/upload/${taskId.value}/url`);
            const hasContent = computed(() => Array.isArray(get(props, 'brief.deliverables')) && get(props, 'brief.deliverables').length > 0);
            const content = computed(() => {
                const allContent = get(props, 'brief.deliverables', []);
                return filter(allContent, (piece) => piece.task_id === taskId.value);
            });
            const featureFlags = useFeatureFlagsStore().data;
            const { getActivationBriefs } = useActivationBriefs();
            const router = useRouter().get();
            const route = useRoute().get();
            const exitRoute =
                route.params.context === 'summary' ? 'v2.activation.tasks.summary' : 'v2.activation.brief.tasks';
            const backRoute = route.params.back_route || 'v2.activation.brief.tasks';
            const context = route.params.context;
            const formatCaption = (caption) => caption.replace(/\n/g, '<br>');

            async function handleUrlUpload() {
                try {
                    isDelivering.value = true;
                    await this.$http.post(`${deliverUrlText.value}?url=${urlText.value}`);

                    if (featureFlags['task-delivery-improvements-enabled']) {
                        const res = await getActivationBriefs(props.contact.id);
                        root.briefs = res.data;
                    }
                    router.go();
                } catch (error) {
                    emit('error', error);
                } finally {
                    isDelivering.value = false;
                }
            }

            async function handleFileUpload(res) {
                const status = get(res, 'file.status');

                if (status === 'done') {
                    if (featureFlags['task-delivery-improvements-enabled']) {
                        const res = await getActivationBriefs(props.contact.id);
                        root.briefs = res.data;
                    }
                    router.go();
                }
            }

            async function handleFeedbackUpload() {
                try {
                    isDelivering.value = true;
                    await this.$http.post(deliverFeedbackUrl.value, {
                        feedback: encodeURIComponent(feedback.value)
                    });
                    if (featureFlags['task-delivery-improvements-enabled']) {
                        const res = await getActivationBriefs(props.contact.id);
                        root.briefs = res.data;
                    }
                    router.go();
                } catch (error) {
                    emit('error', error);
                } finally {
                    isDelivering.value = false;
                }
            }

            function onPageClose() {
                this.$router.push({
                    name: exitRoute,
                    params: { context }
                });
            }

            function onBack() {
                this.$router.push({
                    name: backRoute,
                    params: { context }
                });
            }

            return {
                feedback,
                urlText,
                uploadType,
                deliverFeedbackUrl,
                deliverUrlText,
                deliverContentUrl,
                isDelivering,
                ACTIVATION_TASKS,
                content,
                hasContent,
                handleFileUpload,
                handleUrlUpload,
                handleFeedbackUpload,
                onPageClose,
                onBack,
                taskId,
                moment,
                formatCaption
            };
        }
    };
</script>

<style lang="scss" scoped>
    h3 {
        margin-top: 1em;
        margin-bottom: 1em;
    }

    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    .upload-button {
        height: 40px;
    }

    .border-bottom-grey {
        border-bottom: 1px #b6b6b6 solid;
        margin-bottom: 1em;
    }

    .null-state-container {
        display: flex;
        flex-flow: column;
        justify-content: center;
        align-items: center;
    }

    .content-text-area {
        height: 131px;
        resize: none;
        margin-bottom: 1em;
    }

    .uploads-container {
        display: flex;
        flex-flow: column;

        &-description {
            background: #f0f0f0;
            padding: 1em;
            color: black;
            width: 100%;
            border-radius: 10px;
        }

        &-description-url {
            color: #10b7eb;
            width: 100%;
            border-radius: 10px;
        }
    }

    .input-class {
        width: 100%;
        border: #bcbcbc 1px solid;
        padding: 0.4em;
        border-radius: 5px;
    }
</style>
